import { zonedDate } from '../../utils/date-time';
import moment from 'moment';
import 'moment/locale/pt-br';
import { Recorrencia } from './types';

export const DIAS_ENCERRANDO = 30;

export type RecorrenciaSituacao = 'ativa' | 'encerrando' | 'encerrada';

type RegraRecorrencia = {
    timezone?: string;
    tipo: string;
    frequencia: number | string;
    diaSemana?: string;
    modoMensal?: string;
    ocorrenciaMensal?: string;
    diaMes?: number;
    startAt?: string;
};

const diasSemanaLabels: Record<string, string> = {
    domingo: 'domingo',
    segunda: 'segunda-feira',
    terca: 'terça-feira',
    quarta: 'quarta-feira',
    quinta: 'quinta-feira',
    sexta: 'sexta-feira',
    sabado: 'sábado',
};

const ocorrenciaLabels: Record<string, [string, string]> = {
    primeira: ['Primeira', 'Primeiro'],
    segunda: ['Segunda', 'Segundo'],
    terceira: ['Terceira', 'Terceiro'],
    quarta: ['Quarta', 'Quarto'],
    ultima: ['Última', 'Último'],
};

const isDiaMasculino = (diaSemana?: string) => diaSemana === 'sabado' || diaSemana === 'domingo';

// Descreve a regra em uma frase; mensal sem regra explicita segue o dia da data inicial.
export const describeRegra = (regra: RegraRecorrencia) => {
    const frequencia = Number(regra.frequencia) || 1;
    const dia = regra.diaSemana ? diasSemanaLabels[regra.diaSemana] : '';

    if (regra.tipo === 'dias') {
        return frequencia === 1 ? 'Todos os dias' : `A cada ${frequencia} dias`;
    }

    if (regra.tipo === 'semanas') {
        if (frequencia > 1) return `A cada ${frequencia} semanas${dia ? `, ${dia}` : ''}`;
        if (!dia) return 'Toda semana';
        return `${isDiaMasculino(regra.diaSemana) ? 'Todo' : 'Toda'} ${dia}`;
    }

    if (regra.tipo === 'meses') {
        const ocorrencia = ocorrenciaLabels[regra.ocorrenciaMensal || ''];
        const diaMes = regra.diaMes ?? (regra.startAt ? zonedDate(regra.startAt, regra.timezone).date() : undefined);
        let base = diaMes ? `Todo dia ${diaMes} do mês` : 'Todo mês';

        if (regra.modoMensal === 'diaSemana' && dia && ocorrencia) {
            base = `${ocorrencia[isDiaMasculino(regra.diaSemana) ? 1 : 0]} ${dia} do mês`;
        }

        return frequencia > 1 ? `${base}, a cada ${frequencia} meses` : base;
    }

    return `A cada ${frequencia} ${regra.tipo}`;
};

// Referencia para o formulario: a API gera os agendamentos e pode ajustar a quantidade.
export const estimateOcorrencias = (regra: {
    tipo: string;
    frequencia: number;
    inicio: Date | null;
    fim: Date | null;
}) => {
    if (!regra.inicio || !regra.fim) return 0;

    const inicio = moment(regra.inicio).startOf('day');
    const fim = moment(regra.fim).startOf('day');
    const frequencia = Number(regra.frequencia) || 1;

    if (!fim.isAfter(inicio)) return 0;
    if (regra.tipo === 'meses') return Math.floor(fim.diff(inicio, 'months') / frequencia) + 1;

    const intervalo = regra.tipo === 'semanas' ? frequencia * 7 : frequencia;
    return Math.ceil(fim.diff(inicio, 'days') / intervalo);
};

export const isPeriodoValido = (inicio: Date | null, fim: Date | null) =>
    !!inicio && !!fim && moment(fim).startOf('day').isAfter(moment(inicio).startOf('day'));

export const getServiceNames = (recorrencia?: Partial<Recorrencia> | null): string[] => {
    if (recorrencia?.serviceNames?.length) return recorrencia.serviceNames;

    return [recorrencia?.servicoName, ...(recorrencia?.servicosExtrasNames || [])].filter(Boolean) as string[];
};

export const getDiasRestantes = (recorrencia: Pick<Recorrencia, 'endAt' | 'timezone'>) =>
    zonedDate(recorrencia.endAt, recorrencia.timezone).startOf('day').diff(zonedDate(new Date(), recorrencia.timezone).startOf('day'), 'days');

export const getSituacao = (recorrencia: Pick<Recorrencia, 'endAt' | 'timezone'>): RecorrenciaSituacao => {
    const dias = getDiasRestantes(recorrencia);

    if (dias < 0) return 'encerrada';
    return dias <= DIAS_ENCERRANDO ? 'encerrando' : 'ativa';
};

export const getEncerramentoLabel = (recorrencia: Pick<Recorrencia, 'endAt' | 'timezone'>) => {
    const dias = getDiasRestantes(recorrencia);

    if (dias < 0) return 'Encerrada';
    if (dias === 0) return 'Encerra hoje';
    return dias === 1 ? 'Encerra amanhã' : `Encerra em ${dias} dias`;
};

const capitalizeFirst = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

export const formatProximo = (date: string, timezone?: string) => capitalizeFirst(zonedDate(date, timezone).format('ddd, DD/MM [às] HH:mm'));

export const formatMes = (date: string, timezone?: string) => capitalizeFirst(zonedDate(date, timezone).format('MMMM [de] YYYY'));

export const normalizeSearch = (value?: string | null) =>
    (value || '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .trim();
